

// REPO PATH:  src/front-end/code/slices/rules/tempRules.js
//
// New rules now get a client-side client_id (same pattern as controls'
// id) so the UI can track per-item state — e.g. "was Done clicked on
// this rule" — before the server has assigned an _id. The server can
// store or ignore the field; nothing reads it back.

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    list: [],
    savedList: [],
    tempStatus: 'saved',
    editSnapshot: null    // copy of the rule under edit, for the editor's own Cancel
}

const listsMatch = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const defaultRule = {
    _id: null,
    rule: {
        description:  "",
        deviceID:     "",
        fieldName:    "",
        test:         null,   // >, <, >=, <=, =
        threshold:    null,
        grace_period: null,   // seconds
    },
    alert: {
        time:        null,
        alert_sent:  null,
        on_alert:    false,
    },
    actions: []
    // action shapes:
    // { type: 'sms',      recipients: 'all' }   // 'all' opted-in members, or [user_sub,...] once selection lands
    // { type: 'whatsapp', recipients: 'all' }
    // { type: 'email',    recipients: 'all' }   // legacy saved rules may carry address instead
    // { type: 'message' }
    // { type: 'command',  command_id: '' }
    // { type: 'mqtt',     topic: '', payload: '' }
    // { type: 'http',     method: 'GET', url: '', headers: [], body: '' }
}

const tempRulesSlice = createSlice({
    name: 'tempRules',
    initialState,
    reducers: {

        setTempRules: (state, action) => {
            state.list = action.payload.map(item => ({
                ...item,
                actions: item.actions || [],
            }));
            state.savedList = JSON.parse(JSON.stringify(state.list));
            state.tempStatus = 'saved';
        },

        setSaved: (state) => {
            state.savedList = JSON.parse(JSON.stringify(state.list));
            state.tempStatus = 'saved';
        },

        addTempRule: (state) => {
            const newRule = JSON.parse(JSON.stringify(defaultRule));
            newRule.client_id = crypto.randomUUID();
            state.list.push(newRule);
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

        moveTempRuleUp: (state, action) => {
            const index = action.payload;
            if (index === 0) return;
            [state.list[index - 1], state.list[index]] = [state.list[index], state.list[index - 1]];
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

        moveTempRuleDown: (state, action) => {
            const index = action.payload;
            if (index === state.list.length - 1) return;
            [state.list[index], state.list[index + 1]] = [state.list[index + 1], state.list[index]];
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

        deleteTempRule: (state, action) => {
            state.list.splice(action.payload, 1);
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

        // Editor opened: remember this one rule exactly as it is, so the
        // editor's own Cancel can put it back without touching anything
        // else in the draft (other edits, adds, deletes, reorders).
        snapshotRule: (state, action) => {
            const index = action.payload;
            if (state.list[index]) {
                state.editSnapshot = JSON.parse(JSON.stringify(state.list[index]));
            }
        },

        // Editor "Clear changes": restore the snapshot over the rule and
        // KEEP the snapshot — the editor stays open and Clear can be
        // pressed again after further edits. (For a brand-new rule the
        // snapshot is the blank default, so Clear simply blanks the form.)
        // The snapshot is replaced on the next editor open.
        restoreRuleFromSnapshot: (state, action) => {
            const index = action.payload;
            if (state.editSnapshot && state.list[index]) {
                state.list[index] = JSON.parse(JSON.stringify(state.editSnapshot));
            }
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

        // Update a field inside rule.rule
        editRuleField: (state, action) => {
            const { index, key, value } = action.payload;
            if (state.list[index]) {
                state.list[index].rule[key] = value;
                state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
            }
        },

        // Add a new action to rule.actions
        addAction: (state, action) => {
            const { index, actionType } = action.payload;
            if (!state.list[index]) return;
            if (!state.list[index].actions) state.list[index].actions = [];
            const defaults = {
                // sms/whatsapp no longer carry a typed number. They target the
                // tenant's opted-in members; 'all' = everyone with a consented
                // number on that channel. (A future selection UI swaps 'all'
                // for an array of user_sub.)
                sms:      { type: 'sms',      recipients: 'all' },
                whatsapp: { type: 'whatsapp', recipients: 'all' },
                email:    { type: 'email',    recipients: 'all' },
                message:  { type: 'message' },
                command:  { type: 'command',  command_id: '' },
                mqtt:     { type: 'mqtt',     topic: '', payload: '' },
                http:     { type: 'http',     method: 'GET', url: '', headers: [], body: '' },
            };
            if (defaults[actionType]) {
                state.list[index].actions.push(defaults[actionType]);
                state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
            }
        },

        // Remove an action by its position in the actions array
        removeAction: (state, action) => {
            const { index, actionIndex } = action.payload;
            if (state.list[index]) {
                state.list[index].actions.splice(actionIndex, 1);
                state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
            }
        },

        // Update a field within a specific action
        updateAction: (state, action) => {
            const { index, actionIndex, key, value } = action.payload;
            if (state.list[index]?.actions[actionIndex]) {
                state.list[index].actions[actionIndex][key] = value;
                state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
            }
        },

        // Update a header within an http action
        updateActionHeader: (state, action) => {
            const { index, actionIndex, headerIndex, key, value } = action.payload;
            const headers = state.list[index]?.actions[actionIndex]?.headers;
            if (headers?.[headerIndex]) {
                headers[headerIndex][key] = value;
                state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
            }
        },

        addActionHeader: (state, action) => {
            const { index, actionIndex } = action.payload;
            state.list[index]?.actions[actionIndex]?.headers.push({ key: '', value: '' });
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

        removeActionHeader: (state, action) => {
            const { index, actionIndex, headerIndex } = action.payload;
            state.list[index]?.actions[actionIndex]?.headers.splice(headerIndex, 1);
            state.tempStatus = listsMatch(state.list, state.savedList) ? 'saved' : 'unsaved';
        },

    }
});

export const {
    setTempRules, setSaved,
    addTempRule, deleteTempRule,
    moveTempRuleUp, moveTempRuleDown,
    snapshotRule, restoreRuleFromSnapshot,
    editRuleField,
    addAction, removeAction, updateAction,
    updateActionHeader, addActionHeader, removeActionHeader,
} = tempRulesSlice.actions;

export default tempRulesSlice.reducer;